import { getLineContext } from '@core/core/helpers'
import type { RootState } from '@core/core/store'
import type { ActiveEntity, TabObject } from '@core/core/types'
import { createSelector } from '@reduxjs/toolkit'
import type { CubeId, CubeLinesType, TabId } from 'common/types'

import { cubeLineForTabAdapter } from './cubeLineForTabSlice'

const { selectById } = cubeLineForTabAdapter.getSelectors<RootState>(
  state => state.cubeLinesForTabs,
)

export const selectCubeLineForTabById = (state: RootState, id: TabId | undefined) => {
  if (!id) return

  return selectById(state, id)
}

export const selectCubeLinesForTab = (state: RootState, id: TabId) =>
  selectById(state, id)?.cubeLines

export const selectCubeLinesContextForTab = createSelector(
  [(_, id: TabId) => id, selectCubeLinesForTab, (_, id, tab?: TabObject) => tab],
  (id, cubeLines, tab): ActiveEntity[] => {
    if (!cubeLines) {
      return []
    }
    const cubesContext = tab?.context.filter(entity => entity.entityType === 'cube') ?? []
    const cubesHalfContext = tab?.halfContext.filter(entity => entity.entityType === 'cube') ?? []
    const selectedCubes = [...cubesContext, ...cubesHalfContext]

    const cubeLinesContextForTab = cubeLines.reduce<ActiveEntity[]>((arr, cl) => {
      const isSelect = selectedCubes.some(context => context.entityId === cl.cubeId)
      if (isSelect) {
        arr.push(getLineContext(cl.iLine, 'iLine'))
        arr.push(getLineContext(cl.xLine, 'xLine'))
        arr.push(getLineContext(cl.Hline, 'Hline'))
      }
      return arr
    }, [])
    return cubeLinesContextForTab
  },
)

export const selectCubeLineIdByType = createSelector(
  [
    (_, id: TabId) => id,
    (_, i, cubeId: CubeId) => cubeId,
    (_, i, id, lineType: CubeLinesType) => lineType,
    selectCubeLinesForTab,
  ],
  (id, cubeId, lineType, cubeLines) => {
    const cube = cubeLines?.find(cl => cl.cubeId === cubeId)
    switch (lineType) {
      case 'iLine':
        return cube?.iLine.cubeLineId
      case 'xLine':
        return cube?.xLine.cubeLineId
      case 'Hline':
        return cube?.Hline.cubeLineId
      default:
        return
    }
  },
)
